import * as React from 'react';
import LocalDiv from '../../localization/LocalDiv';
import DeadlineCounter from './DeadlineCounter';
import { css } from '@emotion/core';
import { FLEX_ROW } from '../../layout/flexBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { useRoutes } from '../../navigation/routes';
import { onMobileDevice } from '../../globalStyle/mediaQueries';
import { COLORS } from '../../globalStyle/colors';
import { useAppState } from '../../core/redux/hooks';
import LocalHTMLContainer from '../../localization/LocalHTMLContainer';
import { removeRedundantBackslashes } from '../../localization/removeRedundantBackslashes';
import { useMsgText } from '../../localization/hooks';
import { Str } from '../../localization/translationKeys';


const styles = {
  root:            css`
    align-items: center;
    justify-content: space-between;
    
    ${onMobileDevice} {
      flex-direction: column-reverse;
    }
  `,
  breadcrumb:      css`
    align-items: baseline;
    margin-top: 20px;
    color: ${COLORS.DARK_GREY};
      
    ${onMobileDevice} {
      align-self: flex-start;
    }
  `,
  homeLink:        css`
    text-decoration: none;
    color: ${COLORS.DARK_GREY};
    
    &:hover { 
      opacity: .6;
      cursor:pointer;
    }
  `,
  homeImg:         css`
    font-size: 18px;
    ${onMobileDevice} {
      font-size: 16px;
    }
    margin-bottom: 2px;
  `,
  title:           css`
    font-weight: normal;
    font-size: 26px;
    ${onMobileDevice} {
      font-size: 18px;
    }
    margin: 0 5px;
  `,
  deadlineCounter: css`
    width: 200px;
    color: ${COLORS.DARK_GREY};
    
    ${onMobileDevice} {
      align-self: flex-end;
    }
  `,
};


type Props = {
  title: string;
  deadline: string | Date | undefined;

  /**
   * Whether the home icon links back to the dashboard. Defaults to true; the dashboard itself should pass false so
   * the icon is inert on the page it would link to.
   */
  homeIsLink?: boolean;

  className?: string;
};


/**
 * SurveyHeader: a component that renders a link home, survey deadline and page title.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const SurveyHeader: React.FC<Props> = props => {
  const participantId = useAppState(state => state.auth.currentUser?.personId ?? -1);
  const projectId     = useAppState(state => state.projectSummary?.displayProjectId ?? '');

  const routes = useRoutes(participantId, projectId);

  const dashboardLabel = useMsgText(Str.Dashboard);

  return (
    <LocalDiv css={[FLEX_ROW, styles.root]} className={props.className}>
      <LocalDiv css={[FLEX_ROW, styles.breadcrumb]}>
        {
          props.homeIsLink !== false
            ? (
              <Link to={routes.Project} css={styles.homeLink} aria-label={dashboardLabel}>
                <FontAwesomeIcon icon={faHome} css={styles.homeImg} />
              </Link>
            )
            : <FontAwesomeIcon icon={faHome} css={styles.homeImg} aria-hidden='true' />
        }
        <h1 css={styles.title}>
          <LocalHTMLContainer innerHtml={removeRedundantBackslashes(props.title)} />
        </h1>
      </LocalDiv>

      <DeadlineCounter deadline={props.deadline} css={styles.deadlineCounter} />
    </LocalDiv>
  );
};

export default SurveyHeader;
